import { FormattedMessage } from 'react-intl';
import b64toBlob from 'b64-to-blob';
import validatorIsEmail from 'validator/lib/isEmail';
import { findKey, invert, isObject, isNil } from 'lodash';

// CONSTANTS
import { API_URL } from '../env';
import { CONDITIONAL_FREQUENCY, DAY_OF_WEEK_CRON, DAY_OF_MONTH } from 'constants/reports';
import { SORT_DIRECTION } from 'constants/table';

export * as fuel from './fuel.utils';
export * as immutable from './immutable.utils';
export * as intercom from './intercom.utils';
export * as intl from './intl.utils';
export * as mixpanel from './mixpanel.utils';
export * as monetary from './monetary.utils';
export * as region from './region.utils';
export * as table from './table.utils';
export * as validation from './validation.utils';
export * as sentryUtils from './sentry.utils';
export * from './formikdebug.utils';

export const capitalizeFirstLetter = (string) => {
  const [firstLetter, ...remainder] = string.split('');

  return [firstLetter.toUpperCase(), ...remainder].join('');
};

/**
 * Given a list of `filters` and an optional `sortBy` column, this function returns an object
 * that can be supplied to an axios request in order to filter the resource. The keys returned
 * follow the backend python format for filtering resources
 */
export const getFilters = (filters, sortBy) => {
  const urlParams = new URLSearchParams();

  filters.forEach((filter) => {
    let key = filter.get('field');
    const comparator = filter.get('comparator');

    if (typeof key === 'string' && typeof comparator === 'string') {
      if (filter.get('comparator') !== 'exact') {
        // `<key>__exact=<value>` does not work, use `<key>=<value>`
        key += `__${comparator}`;
      }

      if (filter.get('comparator') === '!exact') {
        key += '!';
      }

      urlParams.append(key, filter.get('value'));
    }
  });

  if (Array.isArray(sortBy)) {
    const [sortField, sortDirection] = sortBy;
    const prefix = sortDirection === SORT_DIRECTION.ASCENDING ? '' : '-';

    if (sortField === 'combined_status') {
      urlParams.append('order_by', `${prefix}availability,${prefix}status`);
    } else {
      urlParams.append('order_by', `${prefix}${sortField}`);
    }
  }

  return urlParams;
};

/**
 * Given `intl` and array of message objects,
 * returns array of translated strings
 */
export const mapToFormattedIntlStrings = (intl, messages) =>
  messages.map((message) => intl.formatMessage({ id: message.id }, message.values));

export const filterUpdatedVehicleInfo = (updatedVehicleData) =>
  updatedVehicleData.filter(
    (value, key) => (typeof value === 'string' || typeof value === 'number') && key !== 'owner'
  );

/**
 * This function creates a blob given a base64 string and makes the browser download the file
 */
export const downloadFileFromBase64 = (base64, fileName, fileType) => {
  const blob = b64toBlob(base64, fileType);
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');

  if (document.body !== null) {
    document.body.appendChild(a);
  }

  a.style.cssText = 'display: none';
  a.target = '_blank';
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
};

/**
 * Checks if the email is a valid email. In the case of a list of emails,
 * if any one of the emails inside the array is invalid, the result will be
 * false
 */
export const isEmail = (email) => {
  if (typeof email === 'string') {
    return validatorIsEmail(email);
  }

  if (email.length === 0) {
    return false;
  }

  for (let index = 0; index < email.length; index++) {
    if (!validatorIsEmail(email[index])) {
      return false;
    }
  }

  return true;
};

export const getDisplayText = (translationId, intl) => {
  const stringExists = Boolean(intl.messages[translationId]);
  return stringExists ? <FormattedMessage id={translationId} /> : translationId;
};

/**
 * Given the values passed to this function (which should come from constants/reports), this function will return
 * a formatted string that can be used by a cronjob to determine the frequency to run a task
 */
export const createCronString = (frequency, dayOfWeek, dayOfMonth, time) => {
  const timeArrayValues = time.split(':');
  const monthValue = '*';
  let dayOfMonthValue = '*';
  let dayOfWeekValue = '*';

  if (frequency === CONDITIONAL_FREQUENCY.weekly) {
    dayOfWeekValue = DAY_OF_WEEK_CRON[dayOfWeek];
  }

  if (frequency === CONDITIONAL_FREQUENCY.monthly) {
    // @ts-expect-error TS(2322): Type 'number' is not assignable to type 'string'.
    dayOfMonthValue = Number(dayOfMonth);
  }

  return `${Number(timeArrayValues[1])} ${Number(
    timeArrayValues[0]
  )} ${dayOfMonthValue} ${monthValue} ${dayOfWeekValue}`;
};

/**
 * Formats the crontab schedule into readable content by js
 */
export const getRevertedCronString = (cron) => {
  const frequencyArrayValues = cron.split(' ');

  // Default value to daily if the other conditions don't match
  // We don't need the month since we don't do yearly reports
  let frequency = CONDITIONAL_FREQUENCY.daily;
  let [minute, hour, dayOfMonth] = frequencyArrayValues;
  let dayOfWeek = frequencyArrayValues[4]; // eslint-disable-line prefer-destructuring

  if (minute.length === 1) {
    minute = `0${minute}`;
  }

  if (hour.length === 1) {
    hour = `0${hour}`;
  }

  const time = `${hour}:${minute}`;

  if (dayOfWeek !== '*') {
    frequency = CONDITIONAL_FREQUENCY.weekly;
    dayOfWeek = invert(DAY_OF_WEEK_CRON)[dayOfWeek];
    dayOfMonth = null;
  } else if (dayOfMonth !== '*') {
    frequency = CONDITIONAL_FREQUENCY.monthly;
    dayOfMonth = DAY_OF_MONTH[Number(dayOfMonth)];
    dayOfWeek = null;
  } else {
    dayOfMonth = null;
    dayOfWeek = null;
  }

  return {
    frequency,
    time,
    dayOfWeek,
    dayOfMonth,
  };
};

export const isNilOrEmpty = (value) => isNil(value) || value === '';

export const isObjectWithEmptyKeys = (value) => isObject(value) && !findKey(value, (val) => !isNilOrEmpty(val));

/** Given a javascript object, returns a javascript object in which key and values have been swapped. */
export const invertObjectKeysAndValues = (object) =>
  Object.entries(object).reduce((ret, entry) => {
    const [key, value] = entry;
    // @ts-expect-error TS(2538): Type 'unknown' cannot be used as an index type.
    ret[value] = key;
    return ret;
  }, {});

/** Checks provided URL, returns URL with added url scheme (`http`, `https`) and back-end domain if required */
export const ensureBackendURL = (url) => {
  if (typeof url === 'string' && !url.startsWith(API_URL) && url.startsWith('/')) {
    return API_URL + url;
  }
  return url;
};
